import React, { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  PaymentRequestButtonElement
} from "@stripe/react-stripe-js"
import {
  attachUserPaymentMethod,
  failedPaymentMethods,
  requestedPaymentMethods,
  paymentMethodsSelector,
  setSelectedPaymentMethodId,
  selectedPaymentMethodIdSelector,
  getPaymentMethods,
  checkStripeFullyConnected
} from "store/user"
import { countries } from "country-codes-flags-phone-codes"
import { routes } from "router"
import { createPaymentIntent, capturePayment, updateEmbeddededBooking } from "store/experiences"
import { BOOKING_SOURCE, BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import { Form, Radio, Typography, Input, Select, Row, Col, Divider, message, Button } from "antd"
import styles from "./ExperienceBooking.module.css"
import AddCardModal from "../../Bookings/BookingForm/AddCardModal"
import NoCardImage from "ui/Icon/DSIcon/NoCardImage"
import PaymentMethodList from "pagesV2/pro/Bookings/BookingForm/PaymentMethodList"
import { numberToCurrency } from "helpers/string"
import { resolveCardFeeConfig, grossUpCardFee } from "utils/bookingCalculations"
import PaypalPayment from "./PaypalPayment"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"

const { Text, Title } = Typography
const { Option } = Select

const PaymentBlock = ({ form, uuid, setPaymentLoading, mode, appliedPromos = [] }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { Paragraph } = Typography
  // Embedded (third-party widget) must always behave as logged-out guest checkout, even when a
  // logged-in session exists in the same browser's shared localStorage.
  const accessToken = mode === BOOKING_MODES.embedded ? null : localStorage.getItem("access_token")
  const selectedPayment = Form.useWatch("paymentMethod", form)
  const {
    experienceById: { experienceDetail },
    experienceBookingData: { bookingSelections },
    bookingInfo
  } = useSelector((state) => state.experiences)

  const paymentMethods = useSelector(paymentMethodsSelector)
  const userData = useSelector((state) => state?.user)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(selectedPaymentMethodId)

  const stripe = useStripe()
  const elements = useElements()

  const [paymentRequest, setPaymentRequest] = useState(null)
  const [canMakePayment, setCanMakePayment] = useState(false)
  const [paymentInfo, setPaymentInfo] = useState(null)
  // Guards the wallet (Apple/Google Pay) capture flow against re-entry. Once the wallet has been
  // charged this stays locked so a repeat event can't create a second charge.
  const isWalletSubmittingRef = useRef(false)

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isAddingCard, setIsAddingCard] = useState(false)

  // The card processing fee is charged only on the amount paid by card NOW. Each payment option's
  // preview is computed from preCardTotal (the total before any card fee) so the labels are correct
  // regardless of which option is currently selected. preCardTotal is set by BookingDetailCard's
  // calc; fall back to the grand total for older state.
  const preCardTotal = Math.max(0, bookingSelections?.preCardTotal ?? bookingSelections?.amount ?? 0)
  const cardFeeConfig = resolveCardFeeConfig({
    customFees: experienceDetail?.assigned_custom_fees,
    platformFeeSetting: experienceDetail?.platform_fee_setting
  })
  const depositBase = (preCardTotal * (experienceDetail?.deposit_percentage || 0)) / 100
  const balanceAmount = preCardTotal - depositBase
  // Deposit now + balance in cash: only the deposit is paid by card, so the fee applies to the deposit.
  const depositAmountCash = depositBase + grossUpCardFee(depositBase, cardFeeConfig)
  // Deposit now + balance later by card: the FULL card fee is taken up front, so the balance later
  // carries no card fee.
  const depositAmountLater = depositBase + grossUpCardFee(preCardTotal, cardFeeConfig)
  // Full payment now: card fee applies to the whole pre-card total.
  const fullAmountToday = preCardTotal + grossUpCardFee(preCardTotal, cardFeeConfig)

  const isPaypalConnected = experienceDetail?.guide?.paypal_account_data?.is_connected || false
  const isStripeConnected = checkStripeFullyConnected(experienceDetail?.guide?.stripe_account_data)

  useEffect(() => {
    // Elements is mounted with the platform publishable key for every visitor, so `stripe` exists
    // even for a guide who never connected Stripe. Without this guard the wallet check below would
    // create a Stripe PaymentIntent as soon as the payment step renders — the backend rejects it
    // with `guide_stripe_account_id can not be blank` and the guest sees a red toast on a booking
    // they can still complete via PayPal.
    if (!isStripeConnected) {
      setCanMakePayment(false)
      setPaymentRequest(null)
      return
    }
    if (stripe && elements && bookingSelections?.amount) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Total",
          amount: Math.round(bookingSelections?.amount * 100)
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ["link"]
      })

      pr.canMakePayment().then(async (result) => {
        if (result) {
          setCanMakePayment(true)
          setPaymentRequest(pr)

          try {
            dispatch(createPaymentIntent(bookingInfo?.data?.id, bookingInfo?.data?.guest_token)).then((res) => {
              setPaymentInfo({ client_secret: res?.client_secret, income_id: res?.income?.id })
            })
          } catch (err) {
            console.error("Failed to get client_secret", err)
          }
        }
      })
    }
  }, [stripe, elements, bookingSelections?.amount, bookingInfo?.data?.guest_token, bookingInfo?.data?.id, dispatch, isStripeConnected])

  useEffect(() => {
    if (!(paymentRequest && paymentInfo?.client_secret && stripe)) return

    // Named handler so the exact reference can be removed on cleanup. Without this, every re-run of
    // this effect (deps like paymentInfo change) would stack another "paymentmethod" listener on the
    // same paymentRequest, causing capturePayment to fire multiple times and — on Firefox — a wedged
    // wallet sheet when the duplicate handlers call ev.complete() with conflicting results.
    const handlePaymentMethod = async (ev) => {
      // Re-entrancy guard: ignore repeat wallet events once a charge is in flight.
      if (isWalletSubmittingRef.current) {
        ev.complete("fail")
        return
      }
      isWalletSubmittingRef.current = true

      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        paymentInfo.client_secret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false }
      )

      if (confirmError) {
        ev.complete("fail")
        isWalletSubmittingRef.current = false
        console.error("Payment confirmation failed:", confirmError)
        return
      }

      ev.complete("success")

      if (paymentIntent.status === "requires_action") {
        const { error } = await stripe.confirmCardPayment(paymentInfo?.client_secret)
        if (error) {
          isWalletSubmittingRef.current = false
          console.error("Action failed:", error)
          return
        }
        return
      }

      if (!paymentIntent.id) {
        isWalletSubmittingRef.current = false
        return
      }
      // The wallet has been charged. Keep isWalletSubmittingRef locked from here on so an
      // unexpected capture status can't lead to a second charge.
      setPaymentLoading(true)
      dispatch(capturePayment(bookingInfo?.data?.id, paymentInfo?.income_id)).then((captureRes) => {
        if (captureRes?.status === "succeeded" || captureRes?.status === "requested") {
          if (captureRes.status === "succeeded") {
            message.success("Booking done successfully!")
          }
          form.resetFields()
          navigate(routes?.embeddedExperienceBookingConfirmation({ uuid, id: bookingInfo?.data?.uuid }))
        } else {
          // Charged but capture returned an unexpected status. Leave the guard locked to prevent
          // a re-charge and tell the guest to contact support.
          message.error("Your payment was received but we couldn't confirm your booking. Please contact support before trying again.")
        }
        setPaymentLoading(false)
      })
    }

    paymentRequest.on("paymentmethod", handlePaymentMethod)
    return () => {
      paymentRequest.off("paymentmethod", handlePaymentMethod)
    }
  }, [
    paymentRequest,
    paymentInfo,
    stripe,
    uuid,
    bookingInfo?.data?.id,
    bookingInfo?.data?.uuid,
    dispatch,
    form,
    navigate,
    setPaymentLoading
  ])

  useEffect(() => {
    if (accessToken && mode) {
      dispatch(getPaymentMethods(userData?.id))
    }
  }, [accessToken, mode, userData?.id, dispatch])

  useEffect(() => {
    const defaultCard = paymentMethods?.find((card) => card.is_default === true)

    if (defaultCard) {
      setDefaultPaymentMethodId(defaultCard.payment_method_id)
    }
  }, [paymentMethods, dispatch])

  const onAttachHandler = (paymentMethodId) => () => {
    dispatch(setSelectedPaymentMethodId(paymentMethodId))
    for (const element of elements._elements) {
      element.clear()
    }
    dispatch(getPaymentMethods(userData?.id))
    onCancelModal()
  }

  const handleAddCard = async () => {
    if (!stripe || !elements) {
      message.error("Stripe is not loaded properly. Please try again later.")
      return false
    }

    let formData
    try {
      formData = await form.validateFields()
    } catch (err) {
      console.error(err)
      message.error("Please fill all required fields correctly.")
      return
    }

    const cardNumberElement = elements.getElement(CardNumberElement)

    if (!cardNumberElement) {
      message.error("Card input is not initialized. Try refreshing the page.")
      return
    }

    const param = {
      type: "card",
      card: cardNumberElement,
      billing_details: {
        name: formData?.cardHolderName,
        address: {
          postal_code: formData?.zip
        }
      }
    }
    dispatch(requestedPaymentMethods())
    setIsAddingCard(true)

    try {
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod(param)

      if (stripeError) {
        dispatch(failedPaymentMethods())

        if (stripeError.type === "card_error") {
          message.error(stripeError.message || "Card error. Please check your card details.")
        } else {
          message.error("Payment method creation failed. Try again.")
        }

        setIsAddingCard(false)
        return false
      }

      const payload = {
        id: userData?.id,
        payment_method_id: paymentMethod.id,
        ...(mode === BOOKING_MODES.embedded && {
          booking_source: BOOKING_SOURCE.embedded,
          guide_id: experienceDetail?.guide?.id
        })
      }

      dispatch(attachUserPaymentMethod(payload)).then((result) => {
        if (result?.error) {
          message.error("Failed to attach card to your profile.")
        } else {
          onAttachHandler(paymentMethod.id)()
          message.success("Card added successfully.")
        }
      })
    } catch (err) {
      console.error(err)
      dispatch(failedPaymentMethods())
      message.error("An unexpected error occurred. Please try again later.")
    } finally {
      setIsAddingCard(false)
    }
  }

  const onCancelModal = () => {
    setIsModalVisible(false)
  }

  const getRadioClassName = (value) => {
    if (selectedPayment === value) {
      return `${styles.radioReverse} ${styles.active}`
    }
    return styles.radioReverse
  }

  const renderSavedPaymentMethods = () => {
    if (paymentMethods?.length === 0) {
      return (
        <div className={styles.container}>
          <NoCardImage className={styles.image} />
          <Paragraph className={styles.text}>You have no payment methods added yet</Paragraph>
          <Button
            variant="link"
            color="primary"
            className={styles.addButton}
            onClick={() => {
              setIsModalVisible(true)
            }}
          >
            Add card
          </Button>
        </div>
      )
    }

    return (
      <>
        <PaymentMethodList
          active={defaultPaymentMethodId}
          paymentMethods={paymentMethods}
          className="vstack gap-15"
          setActive={setDefaultPaymentMethodId}
          existingClientId={userData?.id}
        />
        <Button
          variant="link"
          color="primary"
          onClick={() => setIsModalVisible(true)}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}
        >
          Add card
        </Button>
      </>
    )
  }

  const renderPaymentSection = () => {
    // Saved cards live on the platform Stripe account; they can't be charged for a guide who
    // hasn't connected Stripe, so don't offer them (picking one would enable Submit and hit the
    // payment-intent endpoint).
    if (mode && accessToken && isStripeConnected) {
      return (
        <>
          {renderSavedPaymentMethods()}
          <AddCardModal
            visible={isModalVisible}
            form={form}
            loading={isAddingCard}
            onCancel={() => onCancelModal()}
            onSubmit={() => handleAddCard()}
          />
        </>
      )
    }

    if (isStripeConnected) {
      return (
        <>
          <Form.Item label="Name On Card" name="cardName" rules={[{ required: true, message: "Please enter the name on card" }]}>
            <Input placeholder="Enter full name on card" />
          </Form.Item>
          <Form.Item label="Card Number" name="cardNumber" rules={[{ required: true, message: "Please enter your card number" }]}>
            <div className={styles.stripeInputs}>
              <CardNumberElement
                options={{ showIcon: true }}
                onChange={(value) => {
                  if (value.complete) {
                    form.setFieldValue("cardNumber", true)
                  }
                }}
              />
            </div>
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Expiration Date" name="expiry" rules={[{ required: true, message: "Enter expiry date" }]}>
                <div className={styles.stripeInputs}>
                  <CardExpiryElement
                    onChange={(value) => {
                      if (value.complete) {
                        form.setFieldValue("expiry", true)
                      }
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="CVC" name="cvc" rules={[{ required: true, message: "Enter CVC" }]}>
                <div className={styles.stripeInputs}>
                  <CardCvcElement
                    className={styles.cardInput}
                    onChange={(value) => {
                      if (value.complete) {
                        form.setFieldValue("cvc", true)
                      }
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Country"
                name="country"
                initialValue="United States"
                rules={[{ required: true, message: "Select a country" }]}
              >
                <Select placeholder="Choose a country">
                  {countries?.map((country) => (
                    <Option key={country.code} value={country.name}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="ZIP Code" name="zip" rules={[{ required: true, message: "Enter ZIP code" }]}>
                <Input placeholder="ZIP Code" />
              </Form.Item>
            </Col>
          </Row>
        </>
      )
    }

    return null
  }

  return (
    <>
      <Title level={5} className="mb-m">
        How would you like to pay?
      </Title>
      <Form.Item name="paymentMethod" initialValue="full" style={{ marginBottom: 32 }}>
        <Radio.Group
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
          onChange={(e) => {
            const selected = e?.target?.value
            let payment_option = "full_amount_today"

            if (selected === "later") {
              payment_option = "deposit_and_balance_later"
            } else if (selected === "cash") {
              payment_option = "deposit_and_balance_in_cash"
            }

            dispatch(
              updateEmbeddededBooking(bookingInfo?.data?.uuid, {
                booking: { payment_option }
              })
            )
          }}
        >
          <Radio className={getRadioClassName("full")} value="full">
            <div>
              <Text strong>Pay the total amount today</Text>
              <br />
              <Text type="secondary">
                Your card will be charged the total amount <b>{numberToCurrency(fullAmountToday)}</b> today.
              </Text>
            </div>
          </Radio>
          {!experienceDetail?.full_payment_required && !bookingInfo?.data?.less_then_24h && (
            <>
              <Radio className={getRadioClassName("cash")} value="cash">
                <div>
                  <Text strong>Pay deposit today & the balance in cash</Text>
                  <br />
                  <Text type="secondary">
                    Your card will be charged {numberToCurrency(depositAmountCash)} now & {numberToCurrency(balanceAmount)} in cash on the day
                    of trip.
                  </Text>
                </div>
              </Radio>
              <Radio className={getRadioClassName("later")} value="later">
                <div>
                  <Text strong>Pay deposit today & the balance later</Text>
                  <br />
                  <Text type="secondary">
                    Your card will be charged {numberToCurrency(depositAmountLater)} now & {numberToCurrency(balanceAmount)} 24h before your
                    trip.
                  </Text>
                </div>
              </Radio>
            </>
          )}
        </Radio.Group>
      </Form.Item>
      <Title level={5} className="mb-m">
        {t("booking.payment_methods")}
      </Title>
      {isStripeConnected && (
        <div className={styles.paymentProviderGroup}>
          {canMakePayment && paymentRequest && selectedPayment === "full" && (
            <>
              <div style={{ marginBottom: 24 }}>
                <PaymentRequestButtonElement
                  options={{ paymentRequest }}
                  style={{
                    paymentRequestButton: {
                      type: "default",
                      theme: "dark",
                      height: "40px",
                      width: "100%"
                    }
                  }}
                />
              </div>
              <Divider plain className="my-s">
                or
              </Divider>
            </>
          )}
          {renderPaymentSection()}
        </div>
      )}
      {isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT") && (
        <div className={styles.paymentProviderGroup}>
          <PaypalPayment bookingId={bookingInfo?.data?.id} bookingUuid={bookingInfo?.data?.uuid} promoCodes={appliedPromos.map((p) => p.code)} />
        </div>
      )}
    </>
  )
}

PaymentBlock.propTypes = {
  form: PropTypes.object,
  uuid: PropTypes.string,
  setPaymentLoading: PropTypes.func,
  mode: PropTypes.string,
  appliedPromos: PropTypes.array
}

export default PaymentBlock
